import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { fadeIn } from "@/lib/numa-assets";

const INGREDIENTS = [
  {
    id: "nad-precursor",
    code: "01",
    name: "NAD+ Precursor",
    role: "Choose your path",
    detail:
      "Two variants of the same protocol. NR-05 uses NR Chloride; NMN-05 uses NMN. Both are direct precursors that elevate intracellular NAD+ — the energy currency of the cell.",
    dual: [
      { label: "NR-05", value: "NR Chloride", dose: "350mg" },
      { label: "NMN-05", value: "NMN", dose: "500mg" },
    ],
  },
  {
    id: "pterostilbene",
    code: "02",
    name: "Pterostilbene",
    dose: "50mg",
    role: "Sirtuin signal",
    detail:
      "A methylated stilbene that modulates sirtuin activity and supports longevity-linked signaling pathways alongside NAD+ availability.",
  },
  {
    id: "pqq",
    code: "03",
    name: "PQQ",
    dose: "20mg",
    role: "Mitochondrial optimization",
    detail:
      "Pyrroloquinoline Quinone — supports the growth of new mitochondria and the structural integrity of cellular energy production.",
  },
  {
    id: "fisetin",
    code: "04",
    name: "Fisetin",
    dose: "25mg",
    role: "Cellular maintenance",
    detail:
      "A flavonoid researched for its role in cellular housekeeping and the clearance of aging signal noise across tissues.",
  },
  {
    id: "ergothioneine",
    code: "05",
    name: "Ergothioneine",
    dose: "5mg",
    role: "Redox resilience",
    detail:
      "A rare amino-thione that accumulates in mitochondria-dense tissue. Supports antioxidant defense and cellular continuity under stress.",
  },
];

export const Ingredients = () => {
  return (
    <section
      id="ingredients"
      data-testid="ingredients-section"
      className="bg-white border-t border-[#E2E5E0] py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div {...fadeIn} className="mb-16 sm:mb-24 grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-5">
            <div className="flex items-center gap-4">
              <span className="numa-marker">03</span>
              <span className="h-[1px] w-12 bg-[#8A9A90]" />
              <span className="numa-marker text-[#152219]">
                Ingredient Architecture
              </span>
            </div>
            <h2
              className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-[-0.025em] font-medium text-[#152219] mt-8 leading-[1.04]"
              data-testid="ingredients-title"
            >
              Five engineered
              <br />
              actives. One protocol.
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-6 lg:col-start-7 self-end">
            <p className="text-[15px] leading-[1.7] text-[#57635A] max-w-md">
              Each compound is selected for a specific function and dosed to
              work within the architecture of the others — no excess, no
              proprietary blends. Every batch is third-party tested to verify
              purity and potency.
            </p>
          </div>
        </motion.div>

        {/* Bento collapsed-border grid: 1px gap with parent bg creates hairline borders */}
        <div className="bento-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border border-[#E2E5E0]">
          {INGREDIENTS.map((ing, i) => (
            <motion.article
              key={ing.id}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{
                duration: 0.7,
                delay: i * 0.06,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="p-8 md:p-10 lg:p-12 min-h-[320px] flex flex-col group"
              data-testid={`ingredient-card-${ing.id}`}
            >
              <div className="flex items-start justify-between">
                <span className="numa-marker">{ing.code}</span>
                {ing.dose && (
                  <span className="font-heading text-[15px] text-[#3E5F4A] tracking-tight font-medium font-mono-num">
                    {ing.dose}
                  </span>
                )}
              </div>
              <div className="mt-12">
                <h3 className="font-heading text-2xl tracking-tight text-[#152219] font-medium">
                  {ing.name}
                </h3>
                <p className="text-[11px] tracking-[0.22em] uppercase text-[#8A9A90] mt-2">
                  {ing.role}
                </p>
                <p className="text-[13.5px] leading-[1.65] text-[#57635A] mt-5">
                  {ing.detail}
                </p>
                {ing.dual && (
                  <div
                    className="mt-6 pt-5 border-t border-[#E2E5E0] space-y-2"
                    data-testid={`ingredient-dual-${ing.id}`}
                  >
                    {ing.dual.map((d) => (
                      <div
                        key={d.label}
                        className="flex items-center justify-between text-[12px]"
                      >
                        <span className="tracking-[0.22em] uppercase text-[#8A9A90] text-[10px]">
                          {d.label}
                        </span>
                        <span className="flex items-baseline gap-3">
                          <span className="font-heading text-[#3E5F4A] tracking-tight">
                            {d.value}
                          </span>
                          {d.dose && (
                            <span className="font-heading text-[15px] text-[#3E5F4A] tracking-tight font-medium font-mono-num">
                              {d.dose}
                            </span>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.article>
          ))}
          {/* Final cell: link to /science deep dive */}
          <Link
            to="/science"
            data-testid="ingredients-science-link"
            className="flex p-8 md:p-10 lg:p-12 min-h-[320px] flex-col justify-between group bg-white hover:bg-[#F0F2EF] transition-colors duration-500"
          >
            <div className="flex items-start justify-between">
              <span className="numa-marker">Ref</span>
              <ArrowUpRight
                className="w-5 h-5 text-[#3E5F4A] transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                strokeWidth={1.4}
              />
            </div>
            <div className="mt-12">
              <h3 className="font-heading text-2xl tracking-tight text-[#152219] font-medium">
                Read the science.
              </h3>
              <p className="text-[11px] tracking-[0.22em] uppercase text-[#8A9A90] mt-2">
                Studies behind each active
              </p>
              <p className="text-[13.5px] leading-[1.65] text-[#57635A] mt-5">
                Peer-reviewed citations for NR Chloride, NMN, Pterostilbene,
                PQQ, Fisetin and Ergothioneine — with DOI links and selected
                findings.
              </p>
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Ingredients;
